/**
 * Shared Payment Step for Guided Intake Forms
 *
 * Drops into step-based intake flows (MensIntake/TRT, WomensIntake/HRT).
 * Mirrors the existing GLP-1 payment template logic (SetupIntent fetch +
 * Stripe Elements on the clinic's connected account) but rendered in the
 * dark-neon aesthetic used by Mens/Womens forms.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { ArrowLeft, Loader2, Shield, CheckCircle } from 'lucide-react';
import type { GuidedPaymentStatus } from '@/types/guidedOrder';

const STRIPE_PK = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;
const API_BASE_URL = (
  (import.meta.env.VITE_API_BASE_URL as string | undefined)
  || 'http://localhost:3001/api'
).replace(/\/+$/, '');

// Payment statuses that mean the hold/charge already happened and we should
// skip Stripe entirely (e.g. storefront checkout ran first).
const PAID_STATUSES: ReadonlyArray<GuidedPaymentStatus> = ['hold_placed', 'captured'];

export interface PaymentStepProps {
  token: string;
  isGuided: boolean;
  paymentStatus: GuidedPaymentStatus | null;
  therapyLabel: 'HRT' | 'TRT' | 'Peptide' | 'GLP-1' | 'Wellness';
  /** Parent signals in-flight submit so the step can show a spinner. */
  isSubmitting?: boolean;
  onPaymentConfirmed: (ids: { paymentMethodId: string; setupIntentId: string }) => void;
  onSkip: () => void;
  onBack: () => void;
}

function PaymentFormInner({
  onConfirmed,
}: {
  onConfirmed: (paymentMethodId: string, setupIntentId: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isConfirming, setIsConfirming] = useState(false);
  // Stripe's <PaymentElement> mounts an iframe asynchronously. useStripe
  // and useElements return truthy before that iframe finishes mounting, so
  // we must also gate submit on the element's own `ready` event — otherwise
  // confirmSetup() fails with "Element … ready event has been emitted".
  const [isElementReady, setIsElementReady] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const canSubmit = !!(stripe && elements && isElementReady && !isConfirming);

  const handleSubmit = async () => {
    if (!stripe || !elements || !isElementReady) return;
    setIsConfirming(true);
    setErrorMessage(null);

    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      });

      if (error) {
        setErrorMessage(error.message || 'Unable to confirm payment. Please try again.');
      } else if (setupIntent?.payment_method) {
        const pmId =
          typeof setupIntent.payment_method === 'string'
            ? setupIntent.payment_method
            : setupIntent.payment_method.id;
        onConfirmed(pmId, setupIntent.id);
      } else {
        setErrorMessage('Payment confirmation did not return a payment method.');
      }
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? (err as { message: string }).message
          : 'Payment confirmation failed.';
      setErrorMessage(message);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <PaymentElement
        options={{ layout: 'tabs' }}
        onReady={() => {
          setIsElementReady(true);
          setErrorMessage(null);
        }}
        onChange={(e) => {
          if (e.complete) setErrorMessage(null);
        }}
      />

      {errorMessage && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
          {errorMessage}
        </div>
      )}

      <button
        type="button"
        disabled={!canSubmit}
        onClick={() => { void handleSubmit(); }}
        className="flex w-full items-center justify-center gap-2 rounded-lg px-6 py-3 font-semibold transition-all disabled:cursor-not-allowed disabled:bg-slate-600 disabled:text-slate-400"
        style={
          canSubmit
            ? { background: 'linear-gradient(90deg, #2FFFC7, #3B82F6)', color: '#000' }
            : undefined
        }
      >
        {!isElementReady ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading payment form…
          </>
        ) : isConfirming ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Authorizing…
          </>
        ) : (
          <>
            <CheckCircle className="h-4 w-4" />
            Authorize &amp; Submit
          </>
        )}
      </button>
    </div>
  );
}

export const PaymentStep: React.FC<PaymentStepProps> = ({
  token,
  isGuided,
  paymentStatus,
  therapyLabel,
  isSubmitting = false,
  onPaymentConfirmed,
  onSkip,
  onBack,
}) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [connectedAccount, setConnectedAccount] = useState<string | null>(null);
  // Customer Session client_secret — Stripe requires this signal in
  // the Elements options to render the customer's saved payment
  // methods in the PaymentElement. Without it, returning patients see
  // a blank card form even though their card is attached to the
  // connected-account Customer.
  const [customerSessionClientSecret, setCustomerSessionClientSecret] =
    useState<string | null>(null);
  const [amountCents, setAmountCents] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const shouldSkip = !isGuided || (paymentStatus !== null && PAID_STATUSES.includes(paymentStatus));
  const isLoading = !shouldSkip && !error && !clientSecret;

  // onSkip is captured via ref so effect deps stay on shouldSkip alone —
  // callback identity changes every parent render and shouldn't re-fire skip.
  const onSkipRef = useRef(onSkip);
  useEffect(() => {
    onSkipRef.current = onSkip;
  }, [onSkip]);
  useEffect(() => {
    if (shouldSkip) onSkipRef.current();
  }, [shouldSkip]);

  useEffect(() => {
    if (shouldSkip || !token) return;

    setError(null);
    let cancelled = false;

    const run = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/intakes/setup-intent/${token}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
        const data = await response.json();
        if (cancelled) return;
        if (!response.ok || !data?.success) {
          throw new Error(
            data?.error?.message || data?.message || 'Failed to initialize payment',
          );
        }
        setClientSecret(data.data.clientSecret);
        setConnectedAccount(data.data.stripeAccount || null);
        setCustomerSessionClientSecret(data.data.customerSessionClientSecret ?? null);
        setAmountCents(
          typeof data.data.amountCents === 'number' ? data.data.amountCents : null,
        );
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to initialize payment setup');
      }
    };

    void run();
    return () => { cancelled = true; };
  }, [token, retryCount, shouldSkip]);

  // Only load Stripe once we know the connected account; a platform-account
  // client would be semantically wrong for this SetupIntent.
  const stripePromise = useMemo(() => {
    if (shouldSkip || !STRIPE_PK || !connectedAccount) return null;
    return loadStripe(STRIPE_PK, { stripeAccount: connectedAccount });
  }, [connectedAccount, shouldSkip]);

  if (shouldSkip || isSubmitting) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-10">
        <Loader2 className="h-8 w-8 animate-spin text-[#4A8A9A]" />
        <p className="text-sm text-white/60">Submitting your intake…</p>
      </div>
    );
  }

  return (
    <>
      <h2 className="mb-2 text-2xl font-light" style={{ color: '#E6E9F0' }}>
        Authorize{' '}
        <span style={{ color: '#3B82F6', fontStyle: 'italic' }}>payment</span>
      </h2>
      <p className="mb-6" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
        Save a card for your {therapyLabel} program. You won&apos;t be charged
        until your prescription is approved and ships.
      </p>

      {amountCents != null && amountCents > 0 && (
        <div
          className="mb-4 flex items-baseline justify-between rounded-xl px-4 py-3"
          style={{
            background: 'rgba(74, 138, 154, 0.08)',
            border: '1px solid rgba(74, 138, 154, 0.25)',
          }}
        >
          <span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Program total
          </span>
          <span className="text-lg font-semibold text-white">
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(amountCents / 100)}
          </span>
        </div>
      )}

      <div
        className="mb-6 flex items-center gap-3 rounded-xl px-4 py-3"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Shield className="h-5 w-5 flex-shrink-0 text-[#4A8A9A]" />
        <p className="text-sm leading-snug" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          Your card is{' '}
          <span className="font-semibold text-white">securely stored via Stripe</span>{' '}
          and held only — no charge until fulfillment.
        </p>
      </div>

      {isLoading && (
        <div className="flex flex-col items-center justify-center gap-3 py-10">
          <Loader2 className="h-8 w-8 animate-spin text-[#4A8A9A]" />
          <p className="text-sm text-white/60">Preparing secure payment…</p>
        </div>
      )}

      {error && (
        <div className="flex flex-col items-center gap-4 py-6">
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-center text-sm text-red-300">
            {error}
          </div>
          <button
            type="button"
            onClick={() => {
              setClientSecret(null);
              setCustomerSessionClientSecret(null);
              setRetryCount(c => c + 1);
            }}
            className="text-sm font-medium text-[#2FFFC7] underline underline-offset-2"
          >
            Try again
          </button>
        </div>
      )}

      {!isLoading && !error && clientSecret && stripePromise && (
        <div
          className="rounded-xl p-5"
          style={{
            background: 'rgba(255, 255, 255, 0.02)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Elements
            stripe={stripePromise}
            options={
              {
                clientSecret,
                // Customer Session unlocks saved-card display in
                // PaymentElement. Stripe's @stripe/stripe-js types
                // accept this via the same options object; when it's
                // null we just omit the key.
                ...(customerSessionClientSecret
                  ? { customerSessionClientSecret }
                  : {}),
                appearance: {
                  theme: 'night',
                  variables: {
                    colorPrimary: '#4A8A9A',
                    colorBackground: 'rgba(255, 255, 255, 0.05)',
                    colorText: '#e2e8f0',
                    colorDanger: '#f87171',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    spacingUnit: '4px',
                    borderRadius: '12px',
                    colorTextPlaceholder: 'rgba(255, 255, 255, 0.35)',
                  },
                  rules: {
                    '.Input': {
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                    '.Input:focus': {
                      border: '1px solid rgba(45, 212, 191, 0.5)',
                      boxShadow: '0 0 0 1px rgba(45, 212, 191, 0.25)',
                    },
                    '.Tab': {
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                    },
                    '.Tab--selected': {
                      border: '1px solid rgba(45, 212, 191, 0.4)',
                      backgroundColor: 'rgba(45, 212, 191, 0.08)',
                    },
                    '.Label': { color: 'rgba(255, 255, 255, 0.7)' },
                  },
                },
              } as StripeElementsOptions
            }
          >
            <PaymentFormInner
              onConfirmed={(paymentMethodId, setupIntentId) =>
                onPaymentConfirmed({ paymentMethodId, setupIntentId })
              }
            />
          </Elements>
        </div>
      )}

      <button
        type="button"
        onClick={onBack}
        className="mt-6 flex items-center gap-2 rounded-lg px-4 py-2 text-sm transition-all"
        style={{
          background: 'rgba(255, 255, 255, 0.06)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          color: 'rgba(255, 255, 255, 0.8)',
        }}
      >
        <ArrowLeft className="h-4 w-4" />
        Back to review
      </button>
    </>
  );
};

export default PaymentStep;
